import { memo, ReactNode, useCallback, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { AccountData } from "@markit/common.types";
import { Colors } from "../../utils/colors";
import StandardBorderedContainer from "../../components/Containers/StandardBorderedContainer";
import MarkitLogoBlue from "../../assets/MarkitLogoBlue.png";
import RectangleButton from "../../components/Buttons/RectangleButton";
import { useWindowResize } from "../../hooks/useWindowResize";
import { isDesktop } from "react-device-detect";
import { checkIsDesktop } from "../../utils/screenResponsivenessUtils";
import ProfilePic from "../../components/ProfilePic";
import { truncateString } from "@markit/common.utils";

const PHONE_WIDTH = 312;
const PHONE_HEIGHT = 652;

type PhoneMockupProps = {
  name: string;
  avatar: ReactNode;
  messageLines: string[];
};

// Coded iMessage-style phone mockup used to preview what a subscriber's text
// thread looks like on the Free vs Markit+ plan (replaces static PNG exports).
const PhoneMockup = (props: PhoneMockupProps) => {
  const { name, avatar, messageLines } = props;

  const styles = {
    glassEffect: {
      border: "0.5px solid rgba(255, 255, 255, 0.2)",
      backdropFilter: "blur(20px) saturate(180%)",
      WebkitBackdropFilter: "blur(20px) saturate(180%)",
    },
  };

  return (
    <div
      style={{
        position: "relative",
        width: PHONE_WIDTH,
        height: PHONE_HEIGHT,
        background:
          "radial-gradient(circle at 50% 0%, #2c2c2e 0%, #000000 65%)",
        borderRadius: 37,
        overflow: "hidden",
        flexShrink: 0,
      }}
    >
      <div
        className="Centering"
        style={{
          position: "absolute",
          left: 14.5,
          top: 33,
          width: 35,
          height: 35,
          borderRadius: 35 / 2,
          backgroundColor: "rgba(255, 255, 255, 0.15)",
          ...styles.glassEffect,
        }}
      >
        <Icon
          icon="ion:chevron-back"
          height={16}
          style={{ color: Colors.WHITE }}
        />
      </div>
      <div
        className="ColumnCentering"
        style={{
          position: "absolute",
          top: 33,
          left: 0,
          width: "100%",
        }}
      >
        {avatar}
        <div
          className="AlignedRow"
          style={{
            gap: 4,
            zIndex: 1,
            marginTop: -4,
            padding: "6px 10px",
            borderRadius: 24,
            backgroundColor: "rgba(185, 185, 185, 0.2)",
            ...styles.glassEffect,
          }}
        >
          <span style={{ color: Colors.WHITE, fontSize: 13 }}>
            {truncateString(name, 34, "...")}
          </span>
          <Icon
            icon="ion:chevron-forward"
            height={11}
            style={{ color: Colors.WHITE }}
          />
        </div>
      </div>
      <div
        style={{
          position: "absolute",
          left: 17.5,
          top: 130,
          maxWidth: 161,
          backgroundColor: "#262628",
          borderRadius: 18,
          borderBottomLeftRadius: 4,
          padding: 14,
        }}
      >
        {messageLines.map((line, index) =>
          line === "" ? (
            <div key={index} style={{ height: 14 }} />
          ) : (
            <span
              key={index}
              style={{
                display: "block",
                color: Colors.WHITE,
                fontSize: 14,
                lineHeight: "14px",
              }}
            >
              {line}
            </span>
          )
        )}
        <svg
          width="14"
          height="16"
          viewBox="0 0 13 16"
          style={{ position: "absolute", left: -5, bottom: 1 }}
          fill="none"
        >
          <path
            d="M9.53674e-07 15.6375C6.44507 17.3933 11.3521 12.3454 13 9.60198L12.8169 1.37171C11.0469 1.9204 7.21408 2.41421 6.04225 0C6.22535 9.87633 1.28169 14.8145 9.53674e-07 15.6375Z"
            fill="#262628"
          />
        </svg>
      </div>
      <div
        className="AlignedRow"
        style={{
          position: "absolute",
          bottom: 24,
          left: 14,
          right: 14,
          gap: 8,
        }}
      >
        <div
          className="Centering"
          style={{
            width: 34,
            height: 34,
            borderRadius: 34 / 2,
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            ...styles.glassEffect,
          }}
        >
          <Icon icon="ion:add" height={26} style={{ color: Colors.WHITE }} />
        </div>
        <div
          className="AlignedRowSpaced"
          style={{
            flex: 1,
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            borderRadius: 18,
            padding: "8px 14px",
            ...styles.glassEffect,
          }}
        >
          <span style={{ color: "rgba(255, 255, 255, 0.5)", fontSize: 14 }}>
            Text Message · SMS
          </span>
          <Icon
            icon="ion:mic-outline"
            height={18}
            style={{ color: "rgba(255, 255, 255, 0.5)" }}
          />
        </div>
      </div>
    </div>
  );
};

type LoginOnboardingTextPreviewProps = {
  accountSettings: AccountData;
  continueOnPress: () => void;
  backOnPress: () => void;
};

const LoginOnboardingTextPreview = (props: LoginOnboardingTextPreviewProps) => {
  const { accountSettings, continueOnPress, backOnPress } = props;

  const windowWidth = useWindowResize();
  const isDesktopSize = useMemo(
    () => checkIsDesktop(windowWidth),
    [windowWidth]
  );

  const [currentIndex, setCurrentIndex] = useState(0);

  const FREE_AVATAR = useMemo(
    () => (
      <div
        className="Centering"
        style={{
          zIndex: 2,
          width: 55,
          height: 55,
          borderRadius: 55 / 2,
          backgroundColor: Colors.WHITE,
        }}
      >
        <img
          src={MarkitLogoBlue}
          alt="Markit"
          style={{ width: 28, height: "auto" }}
        />
      </div>
    ),
    []
  );

  const PLUS_AVATAR = useMemo(
    () => (
      <div className="Centering" style={{ zIndex: 2 }}>
        <ProfilePic user={accountSettings} size={55} />
      </div>
    ),
    [accountSettings]
  );

  const TEXT_PREVIEW_ITEMS = useMemo(
    () => [
      {
        label: "Free",
        name: "Markit",
        avatar: FREE_AVATAR,
        messageLines: [
          "Hey John! Thanks for subscribing. I'll use this number to keep you updated.",
          "",
          "Msg&Data rates may apply. Text STOP to opt out.",
        ],
      },
      {
        label: "Markit+",
        name: accountSettings.fullName,
        avatar: PLUS_AVATAR,
        messageLines: [
          "Hey John! Thanks for subscribing. I'll use this number to keep you updated.",
          "",
          "Reply CARD to save my contact.",
          "",
          "Msg&Data rates may apply. Text STOP to opt out.",
        ],
      },
    ],
    [FREE_AVATAR, PLUS_AVATAR, accountSettings.fullName]
  );

  const goToPrevItem = useCallback(() => {
    setCurrentIndex(
      (prev) =>
        (prev - 1 + TEXT_PREVIEW_ITEMS.length) % TEXT_PREVIEW_ITEMS.length
    );
  }, [TEXT_PREVIEW_ITEMS.length]);

  const goToNextItem = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % TEXT_PREVIEW_ITEMS.length);
  }, [TEXT_PREVIEW_ITEMS.length]);

  const renderPhonePreview = useCallback(
    (item: (typeof TEXT_PREVIEW_ITEMS)[number], showBadge?: boolean) => (
      <div
        className="ColumnCentering"
        style={{ width: "100%", position: "relative" }}
      >
        <PhoneMockup
          name={item.name}
          avatar={item.avatar}
          messageLines={item.messageLines}
        />
        {showBadge ? (
          <div
            className="Centering"
            style={{
              position: "absolute",
              top: -20,
              right: -7,
              padding: 14,
              borderRadius: 12,
              backgroundColor:
                item.label === "Free" ? Colors.WHITE : Colors.BLUE5,
              border:
                item.label === "Free"
                  ? `1px solid ${Colors.GRAY11}`
                  : undefined,
            }}
          >
            <span
              className="bodyMedium"
              style={{
                fontWeight: 700,
                color: item.label === "Free" ? Colors.BLACK : Colors.WHITE,
                whiteSpace: "nowrap",
              }}
            >
              {item.label}
            </span>
          </div>
        ) : null}
      </div>
    ),
    []
  );

  const renderTextPreview = useMemo(
    () =>
      isDesktopSize ? (
        <div
          className="ColumnCentering"
          style={{ flex: 1.5, backgroundColor: Colors.WHITE1 }}
        >
          <div className="AlignedRow" style={{ gap: 14 }}>
            {TEXT_PREVIEW_ITEMS.map((item) => (
              <div key={item.label} style={{ width: 312 }}>
                {renderPhonePreview(item, true)}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div
          className="ColumnCentering"
          style={{
            flex: "none",
            backgroundColor: Colors.WHITE1,
            gap: 24,
            paddingBlock: 30,
            width: "100%",
            minWidth: 0,
            overflowX: "hidden",
            boxSizing: "border-box",
          }}
        >
          <div className="ColumnCentering" style={{ gap: 14 }}>
            <div
              className="AlignedRow"
              style={{ gap: 7, justifyContent: "center" }}
            >
              {TEXT_PREVIEW_ITEMS.map((_, index) => (
                <div
                  key={index}
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 100,
                    backgroundColor:
                      currentIndex === index ? Colors.BLUE5 : Colors.GRAY11,
                    transition:
                      "background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                />
              ))}
            </div>
            <div
              className="AlignedRow"
              style={{ gap: 14, justifyContent: "center" }}
            >
              <Icon
                icon="ion:chevron-back"
                height={20}
                style={{ color: Colors.BLACK, cursor: "pointer" }}
                onClick={goToPrevItem}
              />
              <div
                className="Centering"
                style={{
                  padding: "10px 20px",
                  borderRadius: 12,
                  minWidth: 80,
                  backgroundColor:
                    TEXT_PREVIEW_ITEMS[currentIndex].label === "Free"
                      ? Colors.WHITE
                      : Colors.BLUE5,
                  border:
                    TEXT_PREVIEW_ITEMS[currentIndex].label === "Free"
                      ? `1px solid ${Colors.GRAY11}`
                      : undefined,
                }}
              >
                <span
                  className="bodyMedium"
                  style={{
                    fontWeight: 700,
                    color:
                      TEXT_PREVIEW_ITEMS[currentIndex].label === "Free"
                        ? Colors.BLACK
                        : Colors.WHITE,
                    whiteSpace: "nowrap",
                  }}
                >
                  {TEXT_PREVIEW_ITEMS[currentIndex].label}
                </span>
              </div>
              <Icon
                icon="ion:chevron-forward"
                height={20}
                style={{ color: Colors.BLACK, cursor: "pointer" }}
                onClick={goToNextItem}
              />
            </div>
          </div>
          <div
            style={{
              width: "100%",
              maxWidth: 312,
              overflowX: "hidden",
              boxSizing: "border-box",
            }}
          >
            <Carousel
              showArrows={false}
              showStatus={false}
              showThumbs={false}
              showIndicators={false}
              autoPlay
              infiniteLoop
              interval={5000}
              transitionTime={500}
              swipeable={isDesktopSize}
              emulateTouch
              selectedItem={currentIndex}
              onChange={(index) => setCurrentIndex(index)}
            >
              {TEXT_PREVIEW_ITEMS.map((item) => (
                <div key={item.label}>{renderPhonePreview(item)}</div>
              ))}
            </Carousel>
          </div>
        </div>
      ),
    [
      isDesktopSize,
      TEXT_PREVIEW_ITEMS,
      goToPrevItem,
      currentIndex,
      goToNextItem,
      renderPhonePreview,
    ]
  );

  return (
    <div
      className={isDesktopSize ? "RowNormal" : "ColumnNormal"}
      style={{ height: isDesktopSize ? "100%" : "auto", minHeight: "100%" }}
    >
      {isDesktopSize ? renderTextPreview : null}
      <div
        className={isDesktop ? "ColumnCentering" : "ColumnNormal"}
        style={{
          gap: 24,
          flex: 1,
          paddingBlock: isDesktopSize ? 0 : 30,
          paddingInline: isDesktop ? 0 : 14,
        }}
      >
        <div
          className={isDesktopSize ? "ColumnCentering" : "ColumnNormal"}
          style={{ gap: 24, width: isDesktop ? 330 : "100%" }}
        >
          <div
            className={isDesktopSize ? "ColumnCentering" : "ColumnNormal"}
            style={{ gap: 20 }}
          >
            <div>
              <img
                style={{ height: 20, width: "auto", objectFit: "cover" }}
                src={MarkitLogoBlue}
                alt={"Markit AI Logo"}
              />
            </div>
            <span style={{ fontSize: 20, fontWeight: 500 }}>
              Looking great!
            </span>
          </div>
          <StandardBorderedContainer
            containerStyles={{
              backgroundColor: Colors.WHITE1,
              padding: "20px 14px",
              borderRadius: 14,
              border: `0.5px solid ${Colors.GRAY2}`,
            }}
          >
            <span className="bodyMedium">
              Get ready to send texts!
              <br />
              <br />
              If you use a free plan, messages will be sent using the default
              Markit number. Markit+ unlocks a dedicated phone number, two-way
              messaging, and a more personalized texting experience.
            </span>
          </StandardBorderedContainer>
          <div className="ColumnNormal" style={{ gap: 10, width: "100%" }}>
            <RectangleButton
              buttonLabel="Continue"
              onPress={continueOnPress}
              altColor={Colors.BLACK}
              altTextColor={Colors.WHITE}
              containerStyles={{ padding: "14px 10px" }}
            />
            <RectangleButton
              buttonLabel="Update My Info"
              onPress={backOnPress}
              altColor={Colors.WHITE1}
              altTextColor={Colors.BLACK}
              containerStyles={{
                padding: "14px 10px",
                border: `0.5px solid ${Colors.GRAY2}`,
              }}
            />
          </div>
        </div>
      </div>
      {!isDesktopSize ? renderTextPreview : null}
    </div>
  );
};

export default memo(LoginOnboardingTextPreview);
